import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogActions, Typography, Box, Radio, FormControlLabel, Button } from '@mui/material'
import { useTranslator, useNotification } from '@/hooks'
import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { useChangeBulkEligibilityMutationById } from '@/@data'
// Dialog is UI-only; actions are handled by parent via callbacks

interface EligibilityRateConfirmDialogProps {
  open: boolean
  effectiveRate?: number
  selectedCompanyIds?: number[]
  onCancel: () => void
  onApply: (option: 'new_only' | 'all_employees') => void
}

const EligibilityRateConfirmDialog = ({
  open,
  effectiveRate,
  selectedCompanyIds = [],
  onCancel,
  onApply
}: EligibilityRateConfirmDialogProps) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const [option, setOption] = useState<'new_only' | 'all_employees'>('all_employees')
  const bulkEligibilityMutationById = useChangeBulkEligibilityMutationById()

  useEffect(() => {
    if (!open) return
    setOption('new_only')
  }, [open])

  const handleClose = () => {
    onCancel()
  }

  const handleApply = async () => {
    try {
      if (option === 'all_employees') {
        const payload = {
          is_all: 1,
          user_ids: [],
          exclude_user_ids: [],
          eligibility_rate: Number(effectiveRate || 0)
        }

        const ids = selectedCompanyIds && selectedCompanyIds.length > 0 ? selectedCompanyIds : []

        if (ids.length === 0) {
          n.error(__('Please select companies'))

          return
        }

        if (ids.length > 1) {
          const results = await Promise.allSettled(
            ids.map(id => bulkEligibilityMutationById.mutateAsync({ id, payload }))
          )
          
          const successCount = results.filter(r => r.status === 'fulfilled').length
          const failCount = results.length - successCount

          if (successCount > 0) {
            // n.success(__('Eligibility rate updated successfully'))
            onApply(option)
          }

          if (failCount > 0) {
            n.error(__('Some updates failed'))
          }

          return
        }

        await bulkEligibilityMutationById.mutateAsync({ id: ids[0], payload })
        // n.success(__('Eligibility rate updated successfully'))
      }

      onApply(option)
    } catch (e) {
      // n.error(__('Update eligibility rate failed'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth={false}
      sx={{
        width: 800,
        mx: 'auto'
      }}
    >
      <DialogCloseButton
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 8, zIndex: 9999, background: '#fff' }}
      >
        <i className='tabler-x' />
      </DialogCloseButton>
      <DialogContent
        sx={{
          padding: '64px 64px 0 64px'
        }}
      >
        <div className='pb-6'>
          <Typography className='text-center text-[24px] font-medium mb-2'>
            {__('Eligibility Rate Change Confirmation')}
          </Typography>
          <Typography className='text-[15px] text-center text-[#2F2B3D99] mb-2'>
            {__('Please confirm you want to apply new eligibility rate change for Employer')}
          </Typography>
        </div>

        <Box
          className='rounded-[10px] border'
          sx={{
            borderColor: option === 'new_only' ? '#3551F2' : '#E0E0E0',
            backgroundColor: option === 'new_only' ? '#3551F214' : 'transparent',
            mb: 2
          }}
        >
          <Box className='p-4'>
            <FormControlLabel
              className='flex flex-row items-start gap-3'
              control={<Radio checked={option === 'new_only'} onChange={() => setOption('new_only')} sx={{ p: 0.5 }} />}
              label={
                <div>
                  <div className='flex items-center gap-3'>
                    <i
                      className='tabler-settings text-[15px]'
                      style={{ color: option === 'new_only' ? '#3551F2' : undefined }}
                    />
                    <Typography className='text-[15px] font-medium'>{__('Apply New Setting Only')}</Typography>
                  </div>
                  <Typography className='text-[13px] text-[#6E6B7B] pt-1' style={{ paddingLeft: 28 }}>
                    {__('The change will apply only to new employees. Existing records remain unchanged.')}
                  </Typography>
                </div>
              }
              labelPlacement='end'
              sx={{
                alignItems: 'flex-start',
                m: 0,
                width: '100%',
                '& .MuiFormControlLabel-label': {
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%'
                }
              }}
            />
          </Box>
        </Box>

        <Box
          className='rounded-[10px] border mt-6'
          sx={{
            borderColor: option === 'all_employees' ? '#3551F2' : '#E0E0E0',
            backgroundColor: option === 'all_employees' ? '#3551F214' : 'transparent'
          }}
        >
          <Box className='p-4'>
            <FormControlLabel
              className='flex flex-row items-start gap-3'
              control={
                <Radio
                  checked={option === 'all_employees'}
                  onChange={() => setOption('all_employees')}
                  sx={{ p: 0.5 }}
                />
              }
              label={
                <div>
                  <div className='flex items-center gap-3'>
                    <i
                      className='tabler-users text-[15px]'
                      style={{ color: option === 'all_employees' ? '#3551F2' : undefined }}
                    />
                    <Typography className='text-[15px] font-medium'>
                      {__('Apply New Setting To All Employees')}
                    </Typography>
                  </div>
                  <Typography className='text-[13px] text-[#EA5455] pt-1' style={{ paddingLeft: 28 }}>
                    {__(
                      'Apply the new setting to all employees. This new setting will replace all the current setting of employees !!!'
                    )}
                  </Typography>
                </div>
              }
              labelPlacement='end'
              sx={{
                alignItems: 'flex-start',
                m: 0,
                width: '100%',
                '& .MuiFormControlLabel-label': {
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%'
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        className=' pt-6 justify-center'
        sx={{
          padding: '24px 64px 64px 64px'
        }}
      >
        <Button variant='contained' color='primary' onClick={handleApply}>
          {__('Apply')}
        </Button>
        <Button
          variant='contained'
          onClick={onCancel}
          color='secondary'
          sx={{
            color: '#6F83F6',
            background: '#6F83F629',
            boxShadow: 'none'
          }}
        >
          {__('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EligibilityRateConfirmDialog
